/**
 * "Assign controller..." (DEC-256): point a pedal, pad or knob that OPBridge
 * knows at something this item does.
 *
 * WHAT A PEDAL FIRES. A binding in OPBridge names a named action, and the
 * named actions an item offers are exactly the ones "Share with mods"
 * registers (`modShare/ops.ts`: a launcher's play / stop / jump to each
 * section, a grid's cells, a fader's value). So assigning:
 *   1. marks the item `controllerBound`, which makes the share sync register
 *      its actions whether or not it is shared with mods;
 *   2. waits for that sync, and refuses unless the chosen action actually
 *      landed (a binding to an unregistered id does nothing on a press);
 *   3. writes `(page, control) -> action` through `/controllers/bind`.
 * OPBridge then fires it from the controller's own PC, with this device
 * asleep or gone.
 *
 * WHICH CONTROL FITS WHICH ACTION. A knob, fader, expression pedal or wheel
 * sends a value, and OPBridge refuses a value for an action with no input
 * (400, nothing fires). So a value control takes only an action with an
 * input (a fader, a level, an amount), and a switch only one without. An
 * action with several inputs (an XY pad's X and Y) is offered once per input.
 *
 * Bindings are OPBridge's. This device keeps no copy: what an item is bound
 * to is read back from the registry every time it is shown.
 */

import { editItem, type ItemPatch } from '../layout/store.ts';
import type { LayoutItem } from '../layout/projectStore.ts';
import { getConnection } from '../opbridge/store.ts';
import type { ControllerBinding, ControllerControl, ControllerRegistry } from '../opbridge/httpClient.ts';
import { readRegistry, sendsValue, writeBinding } from '../opbridge/controllers.ts';
import { planShare, shareTargets, actionIdFor, needsOf, itemName, NEED_CAPABILITY, type ShareOp } from '../modShare/ops.ts';
import { syncNow, registeredActionIds, shareStatus } from '../modShare/sync.ts';
import { OPVISUAL_LAYERS, cellLabel } from '../opvisual/wire.ts';

/** One thing a control can be pointed at. */
export interface AssignableAction {
  /** Stable key for the UI: the action id, plus the input for a value. */
  key: string;
  actionId: string;
  /** "Play", "Jump to Chorus", "Amount" - the operation, without the item's name. */
  label: string;
  /** The input a value control drives, or null for a press. */
  input: string | null;
  /** Another app's OWN action (OPVisual's `opvisual-col-N` / `opvisual-cell-lL-C`, published by OPVisual itself):
   *  bound directly, nothing of ours to register first (2026-10-23, OPVisual's ask "one catalogue of OPVisual
   *  actions, not two"). */
  external?: true;
}

export type AssignableResult =
  | { ok: true; actions: AssignableAction[]; waiting: number }
  | { ok: false; why: string };

/** The operation's part of a share label ("Song - jump to Chorus" -> "Jump
 *  to Chorus"); an item's only operation, labelled with its bare name, is
 *  "Press". */
function opWords(op: ShareOp, name: string): string {
  const prefix = `${name} - `;
  const words = op.label.startsWith(prefix) ? op.label.slice(prefix.length) : op.label === name ? '' : op.label;
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Press';
}

/** OPVisual's own column and cell actions, as a Grid offers them (2026-10-23): OPVisual publishes `opvisual-col-1..10`
 *  always and `opvisual-cell-l<layer>-<col>` while that cell holds a clip (OPVisual `controllers.rs`), so the Grid binds
 *  to those instead of registering copies of its own. */
const OPV_OWN = /^opvisual-(col-(\d+)|cell-l(\d+)-(\d+))$/;
function opvOwnActions(item: LayoutItem): AssignableAction[] {
  if (item.kind !== 'opvisualGrid') return [];
  const out: AssignableAction[] = [];
  for (let col = 1; col <= item.cols; col++) out.push({ key: `opvisual-col-${col}`, actionId: `opvisual-col-${col}`, label: `Column ${col}`, input: null, external: true });
  for (let layer = 1; layer <= OPVISUAL_LAYERS; layer++) {
    for (let col = 1; col <= item.cols; col++) {
      const id = `opvisual-cell-l${layer}-${col}`;
      out.push({ key: id, actionId: id, label: `Cell ${cellLabel(col, layer)}`, input: null, external: true });
    }
  }
  return out;
}
/** Is `actionId` one of OPVisual's own column / cell actions inside this Grid's columns? */
function isOpvOwnOf(item: LayoutItem | undefined, actionId: string): boolean {
  if (!item || item.kind !== 'opvisualGrid') return false;
  const m = OPV_OWN.exec(actionId);
  if (!m) return false;
  const col = Number(m[2] ?? m[4]);
  return col >= 1 && col <= item.cols;
}

/** What this item offers a controller, on the bridge its presses land on. */
export async function assignableActions(item: LayoutItem): Promise<AssignableResult> {
  const cfg = shareTargets(item)[0];
  if (!cfg) return { ok: false, why: 'No OPBridge is paired, so there is nothing for a controller to fire.' };
  const plan = await planShare(item, cfg);
  if ('unsupported' in plan) return { ok: false, why: plan.unsupported };
  const caps = (getConnection(cfg.id)?.health()?.capabilities ?? {}) as Record<string, unknown>;
  const name = itemName(item);
  const actions: AssignableAction[] = [...opvOwnActions(item)];
  let waiting = 0;
  for (const op of plan.ops) {
    if (needsOf(op).some((n) => caps[NEED_CAPABILITY[n]] !== true)) { waiting++; continue; }
    const actionId = actionIdFor(item.id, op.key);
    const label = opWords(op, name);
    const inputs = op.inputs ?? [];
    if (inputs.length === 0) {
      actions.push({ key: actionId, actionId, label, input: null });
    } else if (inputs.length === 1) {
      actions.push({ key: `${actionId}#${inputs[0]!.name}`, actionId, label, input: inputs[0]!.name });
    } else {
      for (const inp of inputs) {
        actions.push({ key: `${actionId}#${inp.name}`, actionId, label: `${label} ${inp.name.toUpperCase()}`, input: inp.name });
      }
    }
  }
  if (actions.length === 0) {
    return { ok: false, why: waiting > 0 ? 'Everything this does waits for an OPBridge update before a controller can fire it.' : 'This has nothing a controller can fire yet.' };
  }
  return { ok: true, actions, waiting };
}

/** Does this control fit this action (see the header)? */
export function fits(control: ControllerControl, action: AssignableAction): boolean {
  return sendsValue(control.kind) === (action.input !== null);
}

/** Is this binding one of this item's? Action ids are `<itemId>` or `<itemId>.<key>`; for an OPVisual Grid, also
 *  OPVisual's own column and cell actions in its columns (pass the item). */
export function isItemAction(itemId: string, actionId: string, item?: LayoutItem): boolean {
  return actionId === itemId || actionId.startsWith(`${itemId}.`) || isOpvOwnOf(item, actionId);
}

/** This item's bindings in a registry. */
export function bindingsForItem(registry: ControllerRegistry, itemId: string, item?: LayoutItem): ControllerBinding[] {
  return registry.bindings.filter((b) => isItemAction(itemId, b.actionId, item));
}

/** Point `control` at `action` on `pageId`. Throws with words for people. */
export async function assign(
  item: LayoutItem, action: AssignableAction, control: ControllerControl, pageId: string, viaBridgeId?: string,
): Promise<void> {
  if (!fits(control, action)) {
    throw new Error(sendsValue(control.kind)
      ? `${control.code} sends a value, and "${action.label}" takes none. Pick a switch or button, or an action that takes a value.`
      : `${control.code} is a switch, and "${action.label}" needs a value. Pick a knob, fader or expression pedal.`);
  }
  // Another app's own action (OPVisual's columns and cells) is already in OPBridge's list: bind it as it is.
  if (action.external) {
    await writeBinding({ pageId, controlId: control.id, actionId: action.actionId }, viaBridgeId);
    return;
  }
  if (item.controllerBound !== true) editItem(item.id, { controllerBound: true } as ItemPatch);
  await syncNow();
  const landed = [...registeredActionIds(item.id).values()].some((ids) => ids.has(action.actionId));
  if (!landed) {
    const st = shareStatus(item.id);
    const why = 'why' in st ? st.why : st.kind === 'waiting' ? 'it waits for an OPBridge update' : 'OPBridge did not take it';
    throw new Error(`OPBridge could not be told what "${action.label}" does, so the controller would do nothing: ${why}.`);
  }
  await writeBinding({
    pageId, controlId: control.id, actionId: action.actionId,
    ...(action.input !== null ? { input: action.input } : {}),
  }, viaBridgeId);
}

/** Remove one of this item's bindings. When none is left anywhere, the item
 *  stops asking to stay registered (unless it is shared with mods). */
export async function unassign(item: LayoutItem, binding: ControllerBinding, viaBridgeId?: string): Promise<void> {
  await writeBinding({ pageId: binding.pageId, controlId: binding.controlId }, viaBridgeId);
  const after = await readRegistry();
  if (after.ok && bindingsForItem(after.registry, item.id).filter((b) => !isOpvOwnOf(item, b.actionId)).length === 0 && item.controllerBound) {
    editItem(item.id, { controllerBound: false } as ItemPatch);
  }
}
